import { useEffect, useState } from "react";
import { appAxiosInstance } from "../../../api";
import { PaginationControl } from "react-bootstrap-pagination-control";
import { Table } from "react-bootstrap";
import { toast } from "react-toastify";
import { Loader } from "../../../components/Loader/loader";
import s from "./SubscribersList.module.css";

interface ISubscriber {
    id: number;
    userId: number;
    email: string;
    phoneNumber: string;
    name: string;
    subscribeDate: string;
    nextWriteoffDate: string;
    isFreePeriod: boolean;
    isCancelled: boolean;
    clothesCount: number;
    numberOfDayLogin: number;
    summPayments: number;
    statusSubscribe: string;
}

interface ISubscribersResponse {
    countRecords: number;
    subscribers: ISubscriber[];
}

export const SubscribersList = () => {
    const [subscribers, setSubscribers] = useState<ISubscriber[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [allItems, setAllItems] = useState(0);
    const itemsPerPage = 10;

    const getSubscribers = async () => {
        try {
            setIsLoading(true);
            const response = await appAxiosInstance.get<ISubscribersResponse>('/backend/getSubscribers', {
                params: {
                    currentPage,
                    itemsPerPage
                }
            });

            const subscribersList = response.data.subscribers;
            if (subscribersList && subscribersList.length > 0) {
                setSubscribers(subscribersList);
                setAllItems(response.data.countRecords || 0);
            } else {
                setSubscribers([]);
                setAllItems(0);
            }
        } catch (error) {
            toast.error('Ошибка при загрузке подписчиков');
            setSubscribers([]);
            setAllItems(0);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        getSubscribers();
    }, [currentPage]);

    const formatDate = (dateString: string) => {
        if (!dateString) return '—';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('ru-RU', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        } catch {
            return dateString;
        }
    };

    const formatRevenue = (amount: number) => {
        if (!amount || amount === 0) return '0 ₽';
        return `${amount.toFixed(2)} ₽`;
    };

    const getStatusBadge = (subscriber: ISubscriber) => {
        if (subscriber.isCancelled) {
            return <span className={s.statusCancelled}>Отменена</span>;
        }
        if (subscriber.isFreePeriod) {
            return <span className={s.statusTest}>Пробный период</span>;
        }
        return <span className={s.statusActive}>Активная</span>;
    };

    if (isLoading) {
        return (
            <div className={s.loadingContainer}>
                <Loader />
            </div>
        );
    }

    return (
        <div className={s.container}>
            {subscribers && subscribers.length > 0 ? (
                <div>
                    <div className={s.tableWrapper}>
                        <Table className={s.tableSubscribers}>
                            <thead>
                            <tr>
                                <th className={s.colId}>ID</th>
                                <th className={s.colName}>Имя</th>
                                <th className={s.colEmail}>Email</th>
                                <th className={s.colPhone}>Телефон</th>
                                <th className={s.colDate}>Дата оформления</th>
                                <th className={s.colDate}>Дата продления</th>
                                <th className={s.colStatus}>Статус</th>
                                <th className={s.colClothes}>Вещи</th>
                                <th className={s.colDays}>Дней входа</th>
                                <th className={s.colRevenue}>Доход</th>
                            </tr>
                            </thead>
                            <tbody>
                            {subscribers.map((subscriber) => (
                                <tr key={subscriber.id || subscriber.userId}>
                                    <td className={s.colId}>{subscriber.userId || subscriber.id}</td>
                                    <td className={s.colName}>{subscriber.name || '—'}</td>
                                    <td className={s.colEmail}>{subscriber.email || '—'}</td>
                                    <td className={s.colPhone}>{subscriber.phoneNumber || '—'}</td>
                                    <td className={s.colDate}>{formatDate(subscriber.subscribeDate)}</td>
                                    <td className={s.colDate}>{formatDate(subscriber.nextWriteoffDate)}</td>
                                    <td className={s.colStatus}>{getStatusBadge(subscriber)}</td>
                                    <td className={s.colClothes}>{subscriber.clothesCount || 0}</td>
                                    <td className={s.colDays}>{subscriber.numberOfDayLogin || 0}</td>
                                    <td className={s.colRevenue}>{formatRevenue(subscriber.summPayments)}</td>
                                </tr>
                            ))}
                            </tbody>
                        </Table>
                    </div>

                    <div className={s.paginationWrapper}>
                        <PaginationControl
                            page={currentPage}
                            between={4}
                            total={allItems}
                            limit={itemsPerPage}
                            changePage={(page) => setCurrentPage(page)}
                            ellipsis={1}
                        />
                    </div>
                </div>
            ) : (
                <div className={s.emptyState}>
                    <h3>Нет подписчиков</h3>
                    <p>На данный момент нет пользователей с активной подпиской</p>
                </div>
            )}
        </div>
    );
};